import { apiClient } from './apiClient';
import { apiConfig } from '@/config/apiConfig';
import { handleServiceError } from '@/utils/errorHandler';
import { 
  LoginCredentials, 
  LoginResponse, 
  AuthUser, 
  RefreshTokenResponse 
} from '@/types/auth';

export class AuthService {
  private endpoints = apiConfig.endpoints.auth;
  private rememberMeKey = 'rgf_remember_me';

  /**
   * Login user with email and password
   */
  async login(credentials: LoginCredentials): Promise<LoginResponse> {
    try {
      const response = await apiClient.post(this.endpoints.login, {
        email: credentials.email,
        password: credentials.password
      });
      const data = response.data;

      const storage = credentials.rememberMe ? localStorage : sessionStorage;
      
      // Store tokens in appropriate storage
      if (data.token) {
        storage.setItem(apiConfig.auth.tokenKey, data.token);
      }
      if (data.refreshToken) {
        storage.setItem(apiConfig.auth.refreshTokenKey, data.refreshToken);
      }
      
      // Store remember me preference
      if (credentials.rememberMe) {
        localStorage.setItem(this.rememberMeKey, 'true');
      } else {
        localStorage.removeItem(this.rememberMeKey);
      }

      return data;
    } catch (error: any) {
      handleServiceError(error, 'Login');
    }
  }

  /**
   * Logout user and clear tokens
   * Note: Console auth API doesn't provide a logout endpoint, so we only clear local tokens
   */
  async logout(): Promise<void> {
    try {
      // Console auth API doesn't have a logout endpoint
      // Just clear local tokens - JWT tokens are stateless on the server side
      console.log('Performing client-side logout (Console auth API has no logout endpoint)');
      
      // Clear local tokens immediately
      this.clearTokens();
    } catch (error) {
      console.warn('Logout error:', error);
      // Ensure tokens are cleared even if there's an error
      this.clearTokens();
    }
  }

  /**
   * Refresh authentication token
   */
  async refreshToken(): Promise<RefreshTokenResponse> {
    try {
      const refreshToken = this.getRefreshToken();
      
      if (!refreshToken) {
        throw new Error('No refresh token available');
      }

      const response = await apiClient.post(this.endpoints.refresh, { refreshToken });
      const data = response.data;

      // Determine which storage to use based on remember me preference
      const storage = this.isRememberMeEnabled() ? localStorage : sessionStorage;

      // Update stored token in appropriate storage
      if (data.token) {
        storage.setItem(apiConfig.auth.tokenKey, data.token);
      }
      if (data.refreshToken) {
        storage.setItem(apiConfig.auth.refreshTokenKey, data.refreshToken);
      }

      return data;
    } catch (error: any) {
      // Always clear tokens on refresh failure
      this.clearTokens();
      handleServiceError(error, 'Refresh Token');
    }
  }

  /**
   * Validate current token
   */
  async validateToken(): Promise<AuthUser | null> {
    try {
      const token = this.getToken();
      
      
      if (!token) {
        return null;
      }

      // First try to parse the token to get basic user info without backend validation
      // This allows us to work even if the backend is temporarily unavailable
      const tokenData = this.parseToken(token);
      if (tokenData && tokenData.exp) {
        const currentTime = Math.floor(Date.now() / 1000);
        if (tokenData.exp < currentTime) {
          try {
            await this.refreshToken();
            // If refresh successful, get the new token data
            const newToken = this.getToken();
            if (newToken) {
              const newTokenData = this.parseToken(newToken);
              if (newTokenData) {
                return {
                  id: newTokenData.sub || newTokenData.userId,
                  email: newTokenData.email,
                  roles: newTokenData.roles || [],
                  permissions: newTokenData.permissions || [],
                  first_name: newTokenData.first_name || newTokenData.firstName,
                  last_name: newTokenData.last_name || newTokenData.lastName,
                };
              }
            }
          } catch (refreshError) {
            this.clearTokens();
            return null;
          }
        }
      }

      
      try {
        // Try to validate token with backend
        const response = await apiClient.get(this.endpoints.validateToken);
        return response.data;
      } catch (validationError: any) {
        
        // Check if it's a network error or server error vs auth error
        const isNetworkError = !validationError.response || validationError.code === 'NETWORK_ERROR' || validationError.code === 'ECONNABORTED';
        const isServerError = validationError.response?.status >= 500;
        const isAuthError = validationError.response?.status === 401 || validationError.response?.status === 403;
        
        if (isNetworkError || isServerError) {
          // Network/server issues - use local token if valid
          if (tokenData && tokenData.exp && tokenData.exp > Math.floor(Date.now() / 1000)) {
            return {
              id: tokenData.sub || tokenData.userId,
              email: tokenData.email,
              roles: tokenData.roles || tokenData.user_roles || [],
              permissions: tokenData.permissions || tokenData.user_permissions || [],
              first_name: tokenData.first_name || tokenData.firstName,
              last_name: tokenData.last_name || tokenData.lastName,
            };
          }
        } else if (isAuthError) {
          // Authentication error - try refresh token
          try {
            await this.refreshToken();
            
            // If refresh succeeds, try validation again
            const response = await apiClient.get(this.endpoints.validateToken);
            return response.data;
          } catch (refreshError: any) {
            this.clearTokens();
            return null;
          }
        } else {
          // Other errors - try to use local token data as last resort
          if (tokenData && tokenData.exp && tokenData.exp > Math.floor(Date.now() / 1000)) {
            return {
              id: tokenData.sub || tokenData.userId,
              email: tokenData.email,
              roles: tokenData.roles || tokenData.user_roles || [],
              permissions: tokenData.permissions || tokenData.user_permissions || [],
              first_name: tokenData.first_name || tokenData.firstName,
              last_name: tokenData.last_name || tokenData.lastName,
            };
          }
        }
        
        // If we get here, we couldn't validate or use the token
        this.clearTokens();
        return null;
      }
    } catch (error: any) {
      this.clearTokens();
      return null;
    }
  }

  /**
   * Get current authentication token
   */
  getToken(): string | null {
    // Check localStorage first (for remember me), then sessionStorage
    return localStorage.getItem(apiConfig.auth.tokenKey) || 
           sessionStorage.getItem(apiConfig.auth.tokenKey);
  }

  /**
   * Get current refresh token
   */
  getRefreshToken(): string | null {
    // Check localStorage first (for remember me), then sessionStorage
    return localStorage.getItem(apiConfig.auth.refreshTokenKey) || 
           sessionStorage.getItem(apiConfig.auth.refreshTokenKey);
  }

  /**
   * Check if user selected remember me
   */
  isRememberMeEnabled(): boolean {
    return localStorage.getItem(this.rememberMeKey) === 'true';
  }

  /**
   * Check if user is authenticated
   */
  isAuthenticated(): boolean {
    const token = this.getToken();
    return !!token;
  }

  /**
   * Clear all authentication tokens
   */
  clearTokens(): void {
    // Clear tokens from both localStorage and sessionStorage
    localStorage.removeItem(apiConfig.auth.tokenKey);
    localStorage.removeItem(apiConfig.auth.refreshTokenKey);
    sessionStorage.removeItem(apiConfig.auth.tokenKey);
    sessionStorage.removeItem(apiConfig.auth.refreshTokenKey);
    // Clear remember me preference
    localStorage.removeItem(this.rememberMeKey);
  }

  /**
   * Parse JWT token to get user info (basic implementation)
   * Note: This is a basic implementation. For production, consider using a proper JWT library
   */
  parseToken(token: string): any {
    try {
      const payload = token.split('.')[1];
      const decoded = atob(payload);
      return JSON.parse(decoded);
    } catch (error) {
      console.error('Error parsing token:', error);
      return null;
    }
  }

  /**
   * Get user info from current token
   */
  getCurrentUser(): AuthUser | null {
    const token = this.getToken();
    
    if (!token) {
      return null;
    }

    try {
      const tokenData = this.parseToken(token);
      
      if (!tokenData) {
        return null;
      }


      // Extract user information from token
      // Adjust these fields based on your JWT token structure
      const user = {
        id: tokenData.sub || tokenData.userId,
        email: tokenData.email,
        roles: tokenData.roles || tokenData.user_roles || [],
        permissions: tokenData.permissions || tokenData.user_permissions || [],
        first_name: tokenData.first_name || tokenData.firstName,
        last_name: tokenData.last_name || tokenData.lastName,
      };

      return user;
    } catch (error) {
      console.error('Error getting current user from token:', error);
      return null;
    }
  }
}

// Export singleton instance
export const authService = new AuthService();
export default authService;